import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import useGetRegtankOnboardingDetail from "../../../../hooks/useGetRegtankOnboardingDetail";
import useGetLivenessPhotoVideo from "../../../../hooks/useGetLivenessPhotoVideo";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import type {
  RegtankDocumentPhoto,
  RegtankHistoryData,
} from "../Regtank.types";
import { MODAL_TITLES } from "../Regtank.constants";
import { convertToUtcTwelveHours } from "@funded-here-interface/common/src/Utils/formatter";
import NoImageAvailable from "@funded-here-interface/common/src/assets/NoImageAvailable.jpg";
import NoVideoAvailable from "@funded-here-interface/common/src/assets/NoVideoAvailable.jpg";

interface DocumentViewerState {
  titleDocument: string;
  imageDocument: string;
  videoDocument: string;
  timestampDoc: string;
  titleLivenessDocument: string;
  imageLiveness: string;
  videoLiveness: string;
  timestampLiveness: string;
  regtankDetail?: RegtankDocumentPhoto;
}

interface UseDocumentViewerProps {
  token: string;
  verificationHistoryData: RegtankHistoryData[];
  onOpenDocument: () => void;
  onCloseDocument: () => void;
  onOpenImageModal: () => void;
}

/**
 * Custom hook to handle document and media viewing logic
 * Manages fetching and displaying photos/videos for onboarding and liveness checks
 */
export const useDocumentViewer = ({
  token,
  verificationHistoryData,
  onOpenDocument,
  onCloseDocument,
  onOpenImageModal,
}: UseDocumentViewerProps) => {
  const [state, setState] = useState<DocumentViewerState>({
    titleDocument: "",
    imageDocument: "",
    videoDocument: "",
    timestampDoc: "",
    titleLivenessDocument: "",
    imageLiveness: "",
    videoLiveness: "",
    timestampLiveness: "",
  });

  const getRegtankOnboardingDetailMutation = useGetRegtankOnboardingDetail();
  const getLivenessPhotoVideoMutation = useGetLivenessPhotoVideo();

  const handleClick = useCallback(
    (requestId: string, detailNeeded: string) => {
      const requestIds = [requestId];
      const matchData = verificationHistoryData.find(
        (x) => x.requestId === requestId
      );
      const documentImageVidDate = matchData?.documentIdSubmittedDate || "";
      const livenessImageVidDate = matchData?.submittedOn || "";

      const openModalWithDetail = (detail: any) => {
        const map: Record<
          string,
          { title: string; image: string; video: string; timestamp: string }
        > = {
          documentFrontPhotoUrl: {
            title: MODAL_TITLES.documentFront,
            image: detail.frontDocumentUrl,
            video: "",
            timestamp: documentImageVidDate,
          },
          documentBackPhotoUrl: {
            title: MODAL_TITLES.documentBack,
            image: detail.backDocumentUrl,
            video: "",
            timestamp: documentImageVidDate,
          },
          livenessSelfieUrl: {
            title: MODAL_TITLES.livenessPhoto,
            image: detail.selfieUrl,
            video: "",
            timestamp: livenessImageVidDate,
          },
          livenessVideoUrl: {
            title: MODAL_TITLES.livenessVideo,
            image: "",
            video: detail.selfieVideoUrl,
            timestamp: livenessImageVidDate,
          },
        };

        const modalData = map[detailNeeded];
        if (modalData) {
          setState((prev) => ({
            ...prev,
            titleDocument: modalData.title,
            imageDocument: modalData.image,
            videoDocument: modalData.video,
            timestampDoc: modalData.timestamp,
          }));
          onOpenDocument();
        }
      };

      if (
        !state.regtankDetail ||
        state.regtankDetail.requestId !== requestId ||
        (state.regtankDetail.requestId === requestId &&
          new Date(state.regtankDetail.expiredDate) <= new Date())
      ) {
        getRegtankOnboardingDetailMutation.mutate(
          { token, requestIds },
          {
            onSuccess: (data) => {
              if (data.length === 1) {
                const detail = data[0];
                setState((prev) => ({ ...prev, regtankDetail: detail }));
                openModalWithDetail(detail);
              }
            },
            onError: (e) => {
              toast.error((e as Error).message);
              onCloseDocument();
            },
          }
        );
      } else if (state.regtankDetail.requestId === requestId) {
        openModalWithDetail(state.regtankDetail);
      }
    },
    [
      token,
      verificationHistoryData,
      state.regtankDetail,
      getRegtankOnboardingDetailMutation,
      onOpenDocument,
      onCloseDocument,
    ]
  );

  const handleViewPhotoVideoLiveness = useCallback(
    (
      clientSessionId: string,
      sessionId: string,
      userId: string,
      livenessStatus: LivenessWebVerifyStatusEnum,
      confidence: number,
      dataToView: string,
      reason: string
    ) => {
      getLivenessPhotoVideoMutation.mutate(
        {
          token,
          clientSessionId,
          sessionId,
          userId,
          livenessStatus,
          confidence,
          dataToView,
        },
        {
          onSuccess: (data) => {
            const reasonFailure = "There is no face detected";
            if (dataToView === "PHOTO") {
              setState((prev) => ({
                ...prev,
                videoLiveness: "",
                imageLiveness:
                  reason === reasonFailure ? NoImageAvailable : data.image,
                titleLivenessDocument: MODAL_TITLES.livenessPhoto,
                timestampLiveness: convertToUtcTwelveHours(data.timeStamp),
              }));
              onOpenImageModal();
            } else if (dataToView === "VIDEO") {
              setState((prev) => ({
                ...prev,
                imageLiveness: reason === reasonFailure ? NoVideoAvailable : "",
                videoLiveness: reason === reasonFailure ? "" : data.image,
                titleLivenessDocument: MODAL_TITLES.livenessVideo,
                timestampLiveness: convertToUtcTwelveHours(data.timeStamp),
              }));
              onOpenImageModal();
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    },
    [token, getLivenessPhotoVideoMutation, onOpenImageModal]
  );

  const clearRegtankDetail = useCallback(() => {
    setState((prev) => ({ ...prev, regtankDetail: undefined }));
  }, []);

  return {
    ...state,
    handleClick,
    handleViewPhotoVideoLiveness,
    clearRegtankDetail,
    convertToUtcTwelveHours,
  };
};
